import { nativeApi as api } from './nativeHttp';

// Auth endpoints
export const authApi = {
  register: (data) => api.post('/auth/register', data),
  registerPending: (data) => api.post('/auth/register-pending', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
};

export const billingApi = {
  createCheckoutSession: (data) => api.post('/billing/checkout-session', data),
  getSessionStatus: (sessionId, signupSessionId) =>
    api.get('/billing/session-status', {
      params: {
        session_id: sessionId,
        signup_session_id: signupSessionId || '',
      },
    }),
  createPortalSession: () => api.post('/billing/portal-session'),
};

// User endpoints
export const userApi = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Exercise endpoints
export const exerciseApi = {
  getAll: (params) => api.get('/exercises', { params }),
  getById: (id) => api.get(`/exercises/${id}`),
  create: (data) => api.post('/exercises', data),
  update: (id, data) => api.put(`/exercises/${id}`, data),
  delete: (id) => api.delete(`/exercises/${id}`),
};

// Workout endpoints
export const workoutApi = {
  getAll: () => api.get('/workouts'),
  getArchived: () => api.get('/workouts/archived'),
  getById: (id) => api.get(`/workouts/${id}`),
  applySuggestion: (data) => api.post('/workouts/apply-suggestion', data),
  applyPatch: (data) =>
    api.post('/workouts/apply-suggestion', { mode: 'patch', ...data }),
  completeSession: (id) => api.post(`/workouts/${id}/complete-session`),
  sessionActive: (id) => api.get(`/workouts/${id}/session/active`),
  sessionStart: (id, data) => api.post(`/workouts/${id}/session/start`, data || {}),
  sessionRestore: (id) => api.post(`/workouts/${id}/session/start`, { restoreOnly: true }),
  sessionPause: (id, data) => api.post(`/workouts/${id}/session/pause`, data || {}),
  sessionEnd: (id, data) => api.post(`/workouts/${id}/session/end`, data || {}),
  patchSessionExercise: (workoutId, exerciseId, data) =>
    api.patch(`/workouts/${workoutId}/session/exercises/${exerciseId}`, data || {}),
  completeExerciseSets: (workoutId, exerciseId, data) =>
    api.post(`/workouts/${workoutId}/exercises/${exerciseId}/complete-sets`, data || {}),
  archive: (id) => api.post(`/workouts/${id}/archive`),
  unarchive: (id) => api.post(`/workouts/${id}/unarchive`),
  update: (id, data) => api.put(`/workouts/${id}`, data),
  delete: (id) => api.delete(`/workouts/${id}`),
};

// Workout session history
export const workoutSessionApi = {
  getHistory: (params) => api.get('/workout-sessions/history', { params }),
};

// Duo training rooms
export const duoRoomApi = {
  create: (data) => api.post('/duo-rooms', data || {}),
  join: (data) => api.post('/duo-rooms/join', data || {}),
  getByCode: (code) => api.get(`/duo-rooms/${code}`),
  cancel: (code) => api.delete(`/duo-rooms/${code}`),
  complete: (code) => api.post(`/duo-rooms/${code}/complete`),
  approve: (code) => api.post(`/duo-rooms/${code}/approve`),
  requestChange: (code, data) => api.post(`/duo-rooms/${code}/request-change`, data || {}),
  regenerate: (code, data) => api.post(`/duo-rooms/${code}/regenerate`, data || {}),
};

// Series endpoints
export const seriesApi = {
  getAll: (params) => api.get('/series', { params }),
  getById: (id) => api.get(`/series/${id}`),
  create: (data) => api.post('/series', data),
  update: (id, data) => api.put(`/series/${id}`, data),
  delete: (id) => api.delete(`/series/${id}`),
};

// Evaluation endpoints
export const evaluationApi = {
  getAll: (params) => api.get('/evaluations', { params }),
  getById: (id) => api.get(`/evaluations/${id}`),
  getFeedbackPhrases: () => api.get('/evaluations/feedback-phrases'),
  deleteFeedbackPhrase: (phraseId) => api.delete(`/evaluations/feedback-phrases/${phraseId}`),
  create: (data) => api.post('/evaluations', data),
  update: (id, data) => api.put(`/evaluations/${id}`, data),
  delete: (id) => api.delete(`/evaluations/${id}`),
};

export const exerciseEvaluationApi = {
  getAll: (params) => api.get('/exercise-evaluations', { params }),
  getById: (id) => api.get(`/exercise-evaluations/${id}`),
  create: (data) => api.post('/exercise-evaluations', data),
};

// Athlete metrics (FD-07)
export const athleteApi = {
  getHome: () => api.get('/athlete/home'),
  getMetrics: (params) => api.get('/athlete/metrics', { params }),
};

// IA endpoints
export const iaApi = {
  getEngines: () => api.get('/ia/engines'),
  readiness: () => api.get('/ia/readiness'),
  initializeEngines: () => api.post('/ia/engines/initialize'),
  getHistory: (params) => api.get('/ia/history', { params }),
  getUsageSummary: (params) => api.get('/ia/usage-summary', { params }),
  getPerformanceSummary: () => api.get('/ia/performance-summary'),
  getTrainingObjectives: () => api.get('/ia/training-objectives'),
  suggestWorkout: (data) => api.post('/ia/workout-suggestion', data || {}),
  suggestOnboarding: (data) => api.post('/ia/onboarding-suggestions', data || {}),
  exerciseComplete: (data) => api.post('/ia/exercise-complete', data || {}),
  midSessionReplan: (data) => api.post('/ia/mid-session-replan', data || {}),
  midSessionAnalysis: (data) => api.post('/ia/mid-session-analysis', data || {}),
  preSession: (data) => api.post('/ia/pre-session', data || {}),
  analyzeSetDraft: (data) => api.post('/ia/analyze-set-draft', data || {}),
  createIaJob: (data, config) => api.post('/ia/jobs', data || {}, config),
  sessionComplete: (data) =>
    api.post('/ia/jobs', {
      type: 'session_complete',
      payload: data || {},
      contextLabel: 'Resumo da sessão',
    }),
  getIaJob: (id) => api.get(`/ia/jobs/${id}`),
  getActiveIaJobs: () => api.get('/ia/jobs/active'),
  cancelIaJob: (id) => api.delete(`/ia/jobs/${id}`),
};

// Exercise demo videos
export const exerciseVideoApi = {
  lookup: (names) =>
    api.get('/exercise-videos/lookup', {
      params: { names: (names || []).join('|') },
    }),
  list: (params) => api.get('/exercise-videos', { params: params || {} }),
  getCategories: () => api.get('/exercise-videos/categories'),
  getBySlug: (slug) => api.get(`/exercise-videos/${encodeURIComponent(slug)}`),
  fetchStreamBlob: (slug) =>
    api.getBlob(`/exercise-videos/${encodeURIComponent(slug)}/stream`),
  fetchPosterBlob: (slug) =>
    api.getBlob(`/exercise-videos/${encodeURIComponent(slug)}/poster`),
};

export default api;
